import { useEffect, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generar o recuperar visitor_id único
const getVisitorId = () => {
  let visitorId = localStorage.getItem('mobilsafe_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('mobilsafe_visitor_id', visitorId);
  }
  return visitorId;
};

// Obtener número de visitas del visitante
const getVisitCount = () => {
  let count = parseInt(localStorage.getItem('mobilsafe_visit_count') || '0', 10);
  count += 1;
  localStorage.setItem('mobilsafe_visit_count', count.toString());
  return count;
};

// Obtener session_id (nueva por cada sesión del navegador)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('mobilsafe_session_id');
  if (!sessionId) {
    sessionId = 's_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('mobilsafe_session_id', sessionId);
  }
  return sessionId;
};

// Hook para tracking de visitas con tiempo
export const usePageTracking = (pageName) => {
  const startTimeRef = useRef(null);
  const visitIdRef = useRef(null);
  const heartbeatRef = useRef(null);

  useEffect(() => {
    const visitorId = getVisitorId();
    const sessionId = getSessionId();
    const visitCount = getVisitCount();
    const visitId = 'visit_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
    visitIdRef.current = visitId;
    startTimeRef.current = Date.now();

    // Registrar entrada a la página
    const trackEntry = async () => {
      try {
        await axios.post(`${API}/analytics/visit`, {
          visitId: visitId,
          visitorId: visitorId,
          sessionId: sessionId,
          visitNumber: visitCount,
          page: pageName,
          eventType: 'entry',
          referrer: document.referrer || null,
          userAgent: navigator.userAgent,
          screenWidth: window.innerWidth,
          screenHeight: window.innerHeight,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        });
      } catch (error) {
        console.debug('Analytics entry error:', error);
      }
    };

    // Heartbeat cada 30 segundos para saber que sigue activo
    const sendHeartbeat = async () => {
      const timeOnPage = Math.round((Date.now() - startTimeRef.current) / 1000);
      try {
        await axios.post(`${API}/analytics/heartbeat`, {
          visitId: visitId,
          visitorId: visitorId,
          page: pageName,
          timeOnPage: timeOnPage
        });
      } catch (error) {
        console.debug('Analytics heartbeat error:', error);
      }
    };

    // Registrar salida de la página
    const trackExit = () => {
      const timeOnPage = Math.round((Date.now() - startTimeRef.current) / 1000);
      
      // Usar sendBeacon para garantizar que se envía aunque se cierre la página
      const data = JSON.stringify({
        visitId: visitId,
        visitorId: visitorId,
        page: pageName,
        eventType: 'exit',
        timeOnPage: timeOnPage
      });
      
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${API}/analytics/exit`, data);
      } else {
        // Fallback para navegadores antiguos
        axios.post(`${API}/analytics/exit`, JSON.parse(data)).catch(() => {});
      }
    };

    trackEntry();
    
    // Heartbeat cada 30 segundos
    heartbeatRef.current = setInterval(sendHeartbeat, 30000);

    // Detectar cuando el usuario sale de la página
    window.addEventListener('beforeunload', trackExit);
    window.addEventListener('pagehide', trackExit);

    return () => {
      // Limpiar al cambiar de página
      clearInterval(heartbeatRef.current);
      window.removeEventListener('beforeunload', trackExit);
      window.removeEventListener('pagehide', trackExit);
      trackExit();
    };
  }, [pageName]);
};

// Componente wrapper para tracking
const PageTracker = ({ page, children }) => {
  usePageTracking(page);
  return children;
};

export default PageTracker;
